/**
 * Sticky progress bar for Campaign Studio jobs. Mounted globally
 * in AppShell so it survives navigation between sections — user
 * starts a Flow 1 create on /campaign-studio, switches to /ads, the
 * bar follows them and updates live until the job hits a terminal
 * state.
 *
 * Source of truth = `localStorage` key `oneads.cs.active_job_id`.
 *   - Set by `useCreateCampaignFlow1.onSuccess` (in the Studio page).
 *   - Read by this component on every render.
 *   - Cleared by the user dismissing the bar OR auto-cleared 5 min
 *     after a terminal state (so a stale completed bar doesn't follow
 *     the user across days).
 *
 * Renders nothing when no active job. Same pattern as
 * AsaInitialSyncBanner.
 */

import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useCampaignStudioJob } from "@/lib/api-hooks";
import {
  readActiveJobId,
  setActiveJobId,
} from "@/lib/campaign-studio-tracker";
import type { CsJobProgress } from "@/lib/types";
import { jobStatusLabel } from "@/lib/cs-job-labels";

function _stateBucket(job: CsJobProgress): "running" | "ok" | "warn" | "err" {
  if (job.status === "queued" || job.status === "running") return "running";
  if (job.status === "completed") return "ok";
  if (job.status === "partially_failed") return "warn";
  return "err";
}

export function CampaignStudioProgressBar() {
  const [jobId, setJobId] = useState<number | null>(readActiveJobId());
  const qc = useQueryClient();

  // Sync with cross-tab localStorage changes + the in-tab custom event
  // dispatched by `trackCampaignStudioJob`.
  useEffect(() => {
    const onStorage = () => setJobId(readActiveJobId());
    window.addEventListener("storage", onStorage);
    window.addEventListener("cs-job-changed", onStorage);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("cs-job-changed", onStorage);
    };
  }, []);

  const jobQ = useCampaignStudioJob(jobId);
  const job = jobQ.data;
  const loggedRef = useRef<number | null>(null);

  // Record the failure reason once so it survives dismissing the bar — the
  // user asked for the error to be logged because "it's unclear what to do".
  // Goes to the browser console (+ Sentry breadcrumb); the reason is ALSO
  // persisted server-side on campaign_creation_jobs / pending_writes.
  useEffect(() => {
    if (!jobId || !job) return;
    const failed =
      job.status === "failed" ||
      job.status === "partially_failed" ||
      job.failed_units > 0;
    if (failed && loggedRef.current !== jobId) {
      loggedRef.current = jobId;
      console.error(
        `[Campaign Studio] job #${jobId} ${job.status}: ` +
          `${job.error_summary ?? "(no detail)"}`,
        job,
      );
    }
  }, [jobId, job]);

  // A stale handle (job from another org, or cleaned up) 404s — clear
  // it so it stops 404ing on every load + the bar hides. This is what
  // produced the "Campaign creation job not found" / unexpected-error
  // the user hit after switching orgs or revisiting the section.
  useEffect(() => {
    if (jobId !== null && jobQ.isError) {
      setActiveJobId(null, false);
      setJobId(null);
    }
  }, [jobId, jobQ.isError]);

  // Stamp `terminal_at` the moment status transitions out of
  // queued / running so the TTL eviction clock starts.
  useEffect(() => {
    if (!jobId || !job) return;
    if (
      job.status !== "queued" &&
      job.status !== "running" &&
      readActiveJobId() === jobId
    ) {
      setActiveJobId(jobId, true);
      // Job finished — the drainer has already pulled the new
      // campaigns' real serving status from ASA + synthesized the
      // ad-group / keyword mirror rows, so refetch the Ads Manager
      // lists without the user hitting reload. Both the stats views AND
      // the meta-mirror views must be invalidated: a freshly-created
      // campaign has zero traffic → no stats rows, so the Ad Groups tab
      // is sourced entirely from the *mirror* query
      // (["ad-groups-mirror"]). Invalidating only the stats keys left
      // the mirror cached-empty (60s staleTime) → "this campaign has no
      // ad groups" until a manual refresh.
      void qc.invalidateQueries({ queryKey: ["stats-campaigns"] });
      void qc.invalidateQueries({ queryKey: ["stats-ad-groups"] });
      void qc.invalidateQueries({ queryKey: ["stats-keywords"] });
      void qc.invalidateQueries({ queryKey: ["campaigns-mirror"] });
      void qc.invalidateQueries({ queryKey: ["ad-groups-mirror"] });
      void qc.invalidateQueries({ queryKey: ["keywords-mirror"] });
    }
  }, [jobId, job, qc]);

  if (!jobId || !job) return null;

  const bucket = _stateBucket(job);
  // Surface the failure (+ an OK button) even while the job is still
  // "running" — one row can error (e.g. an invalid country) while others are
  // in-flight, and a wedged row could keep the job non-terminal forever. The
  // user must always be able to read the reason and close the bar.
  const hasFailure =
    job.status === "failed" ||
    job.status === "partially_failed" ||
    job.failed_units > 0;
  // §38 — prefer per-step counters when present; fall back to legacy
  // unit-count percentage on rows that haven't started yet (no
  // subtree_progress JSONB stamped yet) so the bar still shows
  // something meaningful from the moment the user clicks Create.
  const stepsTotal = Math.max(1, job.progress_steps_total);
  const stepsDone = Math.min(job.progress_steps_done, stepsTotal);
  const legacyTotal = Math.max(1, job.total_units);
  const legacyCompleted = job.done_units + job.failed_units;
  const useSteps = job.progress_steps_total > 0;
  const pct = useSteps
    ? Math.round((stepsDone / stepsTotal) * 100)
    : Math.round((legacyCompleted / legacyTotal) * 100);

  const fallbackLabel = jobStatusLabel(job.flow, job.status);
  const label =
    bucket === "running" && job.current_action
      ? job.current_action
      : fallbackLabel;

  const eta =
    bucket === "running" ? _formatEta(job.eta_seconds, job.status) : null;

  const dismiss = () => {
    setActiveJobId(null, false);
    setJobId(null);
  };

  return (
    <div
      className={
        "cs-progress" + (bucket === "running" ? "" : ` is-${bucket}`)
      }
    >
      <div className="cs-progress-row">
        {bucket === "running" ? (
          <div className="cs-progress-spinner" />
        ) : (
          <span className={"cs-progress-icon-pill is-" + bucket}>
            {bucket === "ok" ? "✓" : "!"}
          </span>
        )}
        <span className="cs-progress-label">{label}</span>
        <div className="cs-progress-bar">
          <div
            className="cs-progress-fill"
            style={{ width: `${pct}%` }}
          />
        </div>
        <span className="cs-progress-count">
          {useSteps
            ? `${stepsDone}/${stepsTotal}`
            : `${legacyCompleted}/${legacyTotal}`}
        </span>
        {eta && <span className="cs-progress-eta">{eta}</span>}
        {(job.done_units > 0 || job.failed_units > 0) && !useSteps && (
          <span className="cs-progress-tally">
            <span className="pos">+{job.done_units}</span>
            {job.failed_units > 0 && (
              <span className="neg">-{job.failed_units}</span>
            )}
          </span>
        )}
        {/* Always dismissable — even while "running" — so a wedged job that
            never reaches a terminal state can still be closed. */}
        <button
          type="button"
          className="cs-progress-x"
          aria-label="Dismiss"
          title="Dismiss"
          onClick={dismiss}
        >
          ✕
        </button>
      </div>
      {/* Why it failed — the real ASA reason, not just "Job failed" — plus an
          explicit OK button to acknowledge + close. Shown for any failure,
          including a still-"running" job with a failed row. */}
      {hasFailure && (
        <div
          style={{
            marginTop: 6,
            display: "flex",
            alignItems: "flex-start",
            gap: 10,
          }}
        >
          <div
            style={{
              flex: 1,
              fontSize: 12.5,
              color: "var(--c-neg-600)",
              lineHeight: 1.4,
            }}
          >
            {job.error_summary ??
              "Some items failed. The reason is recorded in the logs."}
          </div>
          <button
            type="button"
            className="btn sm"
            onClick={dismiss}
            style={{ flexShrink: 0 }}
          >
            OK
          </button>
        </div>
      )}
      {/* Last-N actions feed — only while running so the bar shrinks
          back to one row when the user dismisses or it auto-evicts. */}
      {bucket === "running" && job.recent_actions.length > 0 && (
        <ul className="cs-progress-log">
          {job.recent_actions
            .slice()
            .reverse()
            .map((a, i) => (
              <li key={`${a.ts}-${i}`} className={`cs-progress-log-item is-${a.kind}`}>
                <span className="cs-progress-log-dot" />
                <span className="cs-progress-log-label">{a.label}</span>
              </li>
            ))}
        </ul>
      )}
    </div>
  );
}

function _formatEta(
  seconds: number | null,
  status: CsJobProgress["status"],
): string | null {
  if (status !== "running") return null;
  if (seconds == null) return "Calculating…";
  if (seconds < 5) return "Finishing up…";
  if (seconds < 60) return `~${seconds}s left`;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  if (m < 10 && s >= 5) return `~${m}m ${s}s left`;
  return `~${m}m left`;
}
